/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { lookupCompany, submitSignup } from '../../api/merchantSignup/merchantSignup';
import {
    CompanyLookupError,
    CompanyLookupFailure,
    SignupError,
} from '../../api/merchantSignup/merchantSignupErrors';
import { config } from '../../config';
import {
    CompanyLookup,
    isSignupCountry,
    SignupCountry,
    SignupFieldPath,
    signupFieldPaths,
} from '../../types/merchantSignup/MerchantSignup';
import {
    firstNameOf,
    formatOrganizationNumber,
    formatPostalCode,
    getOrganizationNumberExample,
    getPhoneExample,
    getPostalCodeExample,
    isOrganizationNumberValid,
    normalizeOrganizationNumber,
    normalizePhone,
} from '../../services/helpers/merchantSignup';
import {
    companyOf,
    initialValuesOf,
    getFieldError,
    getFirstProblem,
    SignupFormValues,
    toSignupRequest,
} from './signupForm';
import CountrySelect from './CountrySelect';
import TextField from './TextField';
import {
    Card,
    Divider,
    ErrorText,
    Field,
    Footer,
    Heading,
    HelperText,
    Hint,
    Logo,
    Page,
    Paragraph,
    PrimaryButton,
    Row,
    SectionHeading,
    Stack,
} from './MerchantSignupPage.styles';

export const MERCHANT_SIGNUP_PATH = '/become-a-walley-merchant';

/** Whether the current URL is the public merchant signup page. */
export const isMerchantSignupPath = (pathname: string = window.location.pathname) =>
    pathname === MERCHANT_SIGNUP_PATH || pathname === `${MERCHANT_SIGNUP_PATH}/`;

/**
 * Whether to show the signup page: the URL is the signup path and the environment's config enables the page
 * (MerchantSignupEnabled in public/config.*.js, true in CI, UAT and locally). Elsewhere the path falls through to
 * the normal sign-in flow.
 */
export const isMerchantSignupPage = (pathname: string = window.location.pathname) =>
    config.MerchantSignupEnabled && isMerchantSignupPath(pathname);

export const DATA_PROTECTION_POLICY_URL = 'https://www.walleypay.com/data-protection/';

/** Query parameters of a prefilled signup link: ?country=NO&organizationNumber=923609016&key=lead-42 */
export const COUNTRY_PARAMETER = 'country';
export const ORGANIZATION_NUMBER_PARAMETER = 'organizationNumber';
export const KEY_PARAMETER = 'key';

export interface SignupPrefill {
    country: SignupCountry;
    organizationNumber: string;
    /** Posted with the form, never shown: ties the signup to e.g. a sales lead or an existing organization. */
    key?: string;
}

/** What a prefilled link carries; a missing or unknown country falls back to SE. */
export const readPrefill = (search: string = window.location.search): SignupPrefill => {
    const parameters = new URLSearchParams(search);
    const countryParameter = parameters.get(COUNTRY_PARAMETER)?.toUpperCase();
    const country: SignupCountry = isSignupCountry(countryParameter)
        ? (countryParameter as SignupCountry)
        : 'SE';

    return {
        country,
        organizationNumber: formatOrganizationNumber(
            country,
            parameters.get(ORGANIZATION_NUMBER_PARAMETER)?.trim() ?? ''
        ),
        key: parameters.get(KEY_PARAMETER)?.trim() || undefined,
    };
};

const countryNames: Record<SignupCountry, string> = {
    SE: 'Swedish',
    NO: 'Norwegian',
    FI: 'Finnish',
    DK: 'Danish',
};

/** The company lookup. idle can be fetched; found and failed stay until the country or number changes. */
type LookupState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'found'; company: CompanyLookup }
    | { status: 'failed' };

interface Confirmation {
    firstName: string;
    companyName: string;
    email: string;
}

const lookupErrorMessages = (country: SignupCountry): Record<CompanyLookupFailure, string> => ({
    notFound: 'No company is registered under this organization number.',
    invalidNumber: `The organization number is not a valid ${countryNames[country]} organization number.`,
    registryUnavailable: 'The company registry is not available right now. Try again in a moment.',
    tooManyRequests: 'Too many attempts. Wait a minute and try again.',
    failed: 'The company information could not be fetched. Try again.',
});

/** The message under the submit button for a signup the API rejected. */
const signupErrorMessage = ({ reason, otherErrors }: SignupError): string => {
    switch (reason) {
        case 'companyNotFound':
            return 'The company registry no longer has a company under this organization number.';
        case 'companyRejected':
            return 'The organization number was not accepted. Check it and fetch the company information again.';
        case 'tooManyRequests':
            return 'Too many attempts. Wait a minute and try again.';
        case 'unavailable':
            return 'The signup could not be received right now. Try again in a moment.';
        case 'rejected':
            return otherErrors.length > 0
                ? otherErrors.join(' ')
                : 'Some details were not accepted. Check the fields marked below.';
        default:
            return 'The signup could not be sent. Try again.';
    }
};

/**
 * Public page where a merchant in SE, NO, FI or DK registers for Walley: the company is looked up by organization
 * number, its address, website, phone and email are prefilled for the user to confirm or correct, and the person who
 * should get Merchant Hub access gives their name, phone and email. The form is one card in two steps, replaced by
 * a confirmation once sent. A link can prefill the country and organization number and carry a key that is posted
 * with the form.
 */
const MerchantSignupPage: React.FC = () => {
    const id = useId();
    const submitHintId = `${id}-submit-hint`;

    const [prefill] = useState(() => readPrefill());
    const [lookupState, setLookupState] = useState<LookupState>({ status: 'idle' });
    const company = lookupState.status === 'found' ? lookupState.company : undefined;
    const [submitError, setSubmitError] = useState<string>();
    const [confirmation, setConfirmation] = useState<Confirmation>();

    // onTouched: a field shows its client-side error once visited; server errors are set on the fields they concern
    const {
        register,
        control,
        handleSubmit,
        setValue,
        resetField,
        setError,
        clearErrors,
        getFieldState,
        trigger,
        formState,
    } = useForm<SignupFormValues>({ mode: 'onTouched', defaultValues: initialValuesOf(prefill) });
    const [country, organizationNumber] = useWatch({
        control,
        name: ['country', 'organizationNumber'],
    });
    const values = useWatch({ control, name: signupFieldPaths });

    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Become a Walley merchant';

        return () => {
            document.title = previousTitle;
        };
    }, []);

    /** Drops the looked-up company and its prefilled details; the Merchant Hub access fields keep their values. */
    const clearCompany = () => {
        setLookupState({ status: 'idle' });
        resetField('company');
        setSubmitError(undefined);
    };

    const changeCountry = (next: SignupCountry) => {
        if (next === country) return;

        setValue('country', next);
        clearErrors('organizationNumber');
        clearCompany();
    };

    /** Validated once visited and before a lookup; an empty number is left to the disabled lookup button. */
    const organizationNumberField = register('organizationNumber', {
        validate: value =>
            !value.trim() ||
            isOrganizationNumberValid(country, value) ||
            `Enter a valid ${
                countryNames[country]
            } organization number, like ${getOrganizationNumberExample(country)}.`,
        onChange: clearCompany,
        onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
            if (isOrganizationNumberValid(country, event.target.value))
                setValue(
                    'organizationNumber',
                    formatOrganizationNumber(country, event.target.value)
                );
        },
    });

    const fetchCompany = async () => {
        if (!(await trigger('organizationNumber'))) {
            setLookupState({ status: 'failed' });
            return;
        }

        setLookupState({ status: 'loading' });
        setSubmitError(undefined);

        try {
            const result = await lookupCompany({
                country,
                organizationNumber: normalizeOrganizationNumber(organizationNumber),
            });

            setLookupState({ status: 'found', company: result });
            setValue('company', companyOf(result));
            clearErrors('company');
        } catch (error) {
            setError('organizationNumber', {
                type: 'lookup',
                message: lookupErrorMessages(country)[(error as CompanyLookupError).reason],
            });
            setLookupState({ status: 'failed' });
        }
    };

    const organizationNumberError = getFieldState('organizationNumber', formState).error?.message;
    const firstProblem = company ? getFirstProblem(country, values) : undefined;
    const canLookup = organizationNumber.trim() !== '' && lookupState.status === 'idle';
    const canSubmit = lookupState.status === 'found' && !firstProblem && !formState.isSubmitting;

    /** Props for a field's TextField: registered with its client-side rule, and losing an API error when edited. */
    const fieldProps = (
        path: SignupFieldPath,
        onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
    ) => ({
        ...register(path, {
            validate: value => getFieldError(country, path, value) ?? true,
            onChange: () => {
                if (getFieldState(path).error?.type === 'server') clearErrors(path);
            },
            onBlur,
        }),
        error: getFieldState(path, formState).error?.message,
    });

    const formatPostalCodeOnBlur = (event: React.FocusEvent<HTMLInputElement>) =>
        setValue('company.address.postalCode', formatPostalCode(country, event.target.value));

    /** Puts the calling code of the country in front of a number typed without one, so the user sees what is sent. */
    const formatPhoneOnBlur =
        (path: 'company.phone' | 'hubUser.phone') => (event: React.FocusEvent<HTMLInputElement>) =>
            setValue(path, normalizePhone(country, event.target.value));

    const submit = handleSubmit(async formValues => {
        if (lookupState.status !== 'found') return;

        const { company: found } = lookupState;

        setSubmitError(undefined);

        try {
            await submitSignup(toSignupRequest(formValues, prefill.key));

            setConfirmation({
                firstName: firstNameOf(formValues.hubUser.name),
                companyName: found.companyName ?? formValues.company.name.trim(),
                email: formValues.hubUser.email.trim(),
            });
        } catch (error) {
            const signupError = error as SignupError;

            signupError.fieldErrors.forEach(({ path, message }) =>
                setError(path, { type: 'server', message })
            );
            setSubmitError(signupErrorMessage(signupError));
        }
    });

    const renderForm = () => (
        <form onSubmit={submit} noValidate>
            <Field>
                <CountrySelect value={country} onChange={changeCountry} />
            </Field>

            <Field>
                <TextField
                    label="Organization number"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder={getOrganizationNumberExample(country)}
                    error={organizationNumberError}
                    {...organizationNumberField}
                />
            </Field>

            <PrimaryButton
                type="button"
                onClick={fetchCompany}
                disabled={!canLookup}
                aria-busy={lookupState.status === 'loading'}
            >
                {lookupState.status === 'loading' ? 'Fetching…' : 'Get company information'}
            </PrimaryButton>

            {company && (
                <>
                    <Divider />

                    <Field>
                        {company.companyName ? (
                            <TextField
                                label="Company name"
                                value={company.companyName}
                                disabled
                                readOnly
                            />
                        ) : (
                            <TextField
                                label="Company name"
                                autoComplete="organization"
                                {...fieldProps('company.name')}
                            />
                        )}
                    </Field>

                    <Field>
                        <SectionHeading>Company details</SectionHeading>
                        <HelperText>
                            Address, website, phone and email are prefilled from the company
                            registry. Change them if needed.
                        </HelperText>
                        <Stack>
                            <TextField
                                label="Street address"
                                autoComplete="street-address"
                                {...fieldProps('company.address.street')}
                            />
                            <Row>
                                <TextField
                                    label="Postal code"
                                    inputMode="numeric"
                                    autoComplete="postal-code"
                                    placeholder={getPostalCodeExample(country)}
                                    {...fieldProps(
                                        'company.address.postalCode',
                                        formatPostalCodeOnBlur
                                    )}
                                />
                                <TextField
                                    label="City"
                                    autoComplete="address-level2"
                                    {...fieldProps('company.address.city')}
                                />
                            </Row>
                            <TextField
                                label="Website"
                                type="url"
                                inputMode="url"
                                autoComplete="url"
                                placeholder="www.yourstore.com"
                                {...fieldProps('company.website')}
                            />
                            <TextField
                                label="Company phone number"
                                type="tel"
                                autoComplete="off"
                                placeholder={getPhoneExample(country)}
                                {...fieldProps('company.phone', formatPhoneOnBlur('company.phone'))}
                            />
                            <TextField
                                label="Company email address"
                                type="email"
                                autoComplete="off"
                                placeholder="info@company.com"
                                {...fieldProps('company.email')}
                            />
                        </Stack>
                    </Field>

                    <Divider />

                    <Field>
                        <SectionHeading>Your Merchant Hub access</SectionHeading>
                        <HelperText>We&apos;ll send you an invite to Merchant Hub.</HelperText>
                        <Stack>
                            <TextField
                                label="Full name"
                                autoComplete="name"
                                {...fieldProps('hubUser.name')}
                            />
                            <TextField
                                label="Phone number"
                                type="tel"
                                autoComplete="tel"
                                placeholder={getPhoneExample(country)}
                                {...fieldProps('hubUser.phone', formatPhoneOnBlur('hubUser.phone'))}
                            />
                            <TextField
                                label="Email address"
                                type="email"
                                autoComplete="email"
                                placeholder="name@company.com"
                                {...fieldProps('hubUser.email')}
                            />
                        </Stack>
                    </Field>

                    <PrimaryButton
                        type="submit"
                        disabled={!canSubmit}
                        aria-busy={formState.isSubmitting}
                        aria-describedby={submitHintId}
                    >
                        {formState.isSubmitting ? 'Sending…' : 'Become a merchant'}
                    </PrimaryButton>
                    <Hint id={submitHintId}>
                        {firstProblem ?? "You'll get an email with your Merchant Hub invite."}
                    </Hint>
                    {submitError && <ErrorText role="alert">{submitError}</ErrorText>}
                </>
            )}
        </form>
    );

    return (
        <Page>
            <Card aria-labelledby={`${id}-heading`}>
                <Heading id={`${id}-heading`}>Become a Walley merchant</Heading>
                {confirmation ? (
                    <Paragraph role="status" aria-live="polite">
                        Thanks, {confirmation.firstName}! We&apos;ve sent a Merchant Hub invite for{' '}
                        <strong>{confirmation.companyName}</strong> to{' '}
                        <strong>{confirmation.email}</strong>.
                    </Paragraph>
                ) : (
                    renderForm()
                )}
            </Card>
            <Footer>
                <span>
                    Already a merchant? <a href="/">Sign in</a>
                </span>
                <a href={DATA_PROTECTION_POLICY_URL} target="_blank" rel="noopener noreferrer">
                    Data protection policy
                </a>
                <Logo src="/img/walley-white-logo.png" alt="Walley" />
            </Footer>
        </Page>
    );
};

export default MerchantSignupPage;
